<template>
  <PageLayout>
    <div class="container">
      <div class="box">
        <div class="mb-l">
          <h1 class="title mb-s">Уточнить данные кружка</h1>
          <p>
            Чтобы ваш кружок отобразился на карте проекта, укажите следующую
            информацию.
          </p>
          <p>
            В дальнейшем вы сможете отредактировать ее, если что-то изменится.
          </p>
        </div>
        <ValidationObserver
          ref="form"
          tag="form"
          class="c-form"
          @submit.prevent="handleSubmit"
        >
          <BaseLoadingBox :pending="pending">
            <div class="form-group">
              <div>
                <label class="form-label is-required" for="address">
                  Адрес:
                </label>
                <ValidationProvider
                  v-slot="{ errors }"
                  name="address"
                  mode="aggressive"
                  rules="required"
                >
                  <AddressInput
                    id="address"
                    v-model="address"
                    :errors="errors"
                  />
                </ValidationProvider>
                <span class="color-gray-dark text-size-normal"
                  >Кружок с&nbsp;таким адресом появится на&nbsp;карте
                  проекта</span
                >
              </div>
              <div>
                <label class="form-label is-required" for="name">
                  Название:
                </label>
                <BaseInputWithValidation
                  id="name"
                  v-model="form.name"
                  name="name"
                  rules="required|max:200|caps-count-limit:30"
                  type="text"
                />
                <span class="color-gray-dark text-size-normal"
                  >Максимум 200 символов</span
                >
              </div>
              <div>
                <label class="form-label is-required" for="description">
                  Описание:
                </label>
                <ValidationProvider
                  v-slot="{ errors }"
                  name="description"
                  tag="div"
                  mode="aggressive"
                  rules="required|max:1000"
                >
                  <MarkdownCkComponent
                    v-model="form.description"
                    :is-error="!!errors && !!errors.length"
                  />
                  <span
                    v-if="errors && errors.length"
                    class="form-error md-error"
                    >{{ errors[0] }}</span
                  >
                </ValidationProvider>
                <span class="color-gray-dark text-size-normal mt-s"
                  >Описание будет видно в карточке мероприятия и раскроет суть
                  события. Постарайтесь уложиться в 1500 символов</span
                >
              </div>

              <div>
                <label class="form-label is-required" for="phone">
                  Укажите контактный номер телефона:
                </label>
                <BaseInputWithValidation
                  id="phone"
                  v-model="form.contacts[0].value"
                  name="phone"
                  :format="{
                    phone: true,
                    phoneRegionCode: 'RU',
                  }"
                  type="phone"
                  rules="required"
                />
                <span class="color-gray-dark text-size-normal mt-s"
                  >Этот номер будет указан как контакт кружка для
                  участников</span
                >
              </div>

              <div>
                <p class="text-size-normal color-gray-dark mb-s">
                  Загрузите обложку площадки:
                </p>
                <div>
                  <ValidationProvider
                    v-slot="{ errors }"
                    name="cover"
                    mode="aggressive"
                    :rules="{
                      image: true,
                      size: MAX_LOGO_SIZE,
                    }"
                  >
                    <PlatformCoverBlock
                      ref="platform_cover"
                      v-model="cover"
                      :errors="errors"
                      :cover="coverPreview"
                      @deletePreview="coverPreview = ''"
                    />
                  </ValidationProvider>
                </div>
              </div>
            </div>
          </BaseLoadingBox>

          <div class="form-row mt-s text-center" v-if="error && error.length">
            <div class="form-error">
              {{ error[0] }}
            </div>
          </div>

          <div class="buttons">
            <BaseButton type="submit" :disabled="pending">Создать</BaseButton>
            <BaseButton
              theme="secondary"
              @click="$router.push('/')"
              :disabled="pending"
              >Назад</BaseButton
            >
          </div>
        </ValidationObserver>
      </div>
    </div>
  </PageLayout>
</template>
<script>
import PageLayout from "@/components/PageLayout.vue";
import AddressInput from "@/components/AddressInput.vue";
import PlatformCoverBlock from "@/components/PlatformCoverBlock.vue";
import MarkdownCkComponent from "@/components/MarkdownCkComponent.vue";
import { geoService } from "@/services/geoService";
import { platformsService } from "@/services/platforms";
import { parseError } from "@/utils/parseError";

import { MAX_LOGO_SIZE } from "@/constants";

export default {
  name: "PlatformCreate",
  components: {
    PageLayout,
    AddressInput,
    PlatformCoverBlock,
    MarkdownCkComponent,
  },
  data() {
    return {
      pending: false,
      error: null,
      form: {
        address: "",
        description: `Кружок - участник конкурса кружков ${new Date().getFullYear()} года`,
        name: "",
        latitude: "",
        longitude: "",
        contacts: [
          {
            ordering: 1,
            type: "phone",
            value: "",
          },
        ],
      },
      cover: null,
      coverPreview: "",
    };
  },
  computed: {
    MAX_LOGO_SIZE: () => MAX_LOGO_SIZE,
    selectedOrganization() {
      return this.$store.state.requestPreparing.selectedOrganization;
    },
    address: {
      get() {
        return this.form.address;
      },
      set(val) {
        this.form.address = val?.value || "";
        this.form.latitude = val?.geo_lat || "";
        this.form.longitude = val?.geo_lon || "";
        if (val?.value && (!val.geo_lat || !val.geo_lon)) {
          this.getYandexCoords(val.value);
        }
      },
    },
  },
  created() {
    this.form.name =
      this.selectedOrganization?.full_name ||
      this.selectedOrganization?.short_name ||
      "";
  },
  methods: {
    async getYandexCoords(value) {
      const data = await geoService.getNearestCoordinates(value);
      if (data) {
        this.form.latitude = data.lat;
        this.form.longitude = data.lon;
      }
    },
    scrollToError() {
      const error = this.$el.querySelector(".form-error");
      if (error) {
        error.scrollIntoView({
          block: "center",
        });
      }
    },
    async formValidate() {
      const isValid = await this.$refs.form.validate();

      if (!isValid) {
        setTimeout(() => {
          this.scrollToError();
        }, this.scrollTimeout);
      }
      return isValid;
    },
    async handleSubmit() {
      if (this.pending) return;
      if (!this.selectedOrganization) {
        this.error = [
          "Вы не выбрали организацию, пожалуйста вернитесь на главную страницу и выберите организацию",
        ];
      }
      const isValid = await this.formValidate();
      if (!isValid) {
        this.$nextTick(this.scrollToError);
        return;
      }
      if (!this.form.latitude || !this.form.longitude) {
        this.$refs.form.setErrors({
          address: [
            "У данного адреса отсутствуют географические координаты (долгота, широта). Пожалуйста укажите другой адрес",
          ],
        });
        this.$nextTick(this.scrollToError);
        return;
      }
      try {
        this.pending = true;
        this.error = null;
        const form = { ...this.form, contacts: [...this.form.contacts] };
        form.subject = true;
        form.access_for_all = false;
        if (this.cover) {
          try {
            const file = await this.$refs.platform_cover.cropImage();
            const fd = new FormData();
            fd.append("file", file);
            const { data: coverImageId } = await platformsService.uploadFile({
              data: fd,
              orgId: this.selectedOrganization.id,
            });
            form.cover = coverImageId;
          } catch (error) {
            console.log("error", error);
            this.error = [parseError(error).$first];
            this.pending = false;
            return;
          }
        }
        const { data } = await platformsService.createPlatform({
          orgId: this.selectedOrganization.id,
          payload: form,
        });
        const orgPlatforms =
          this.$store.state.requestPreparing.organizationsPlatforms[
            this.selectedOrganization.id
          ];
        const updatedPlatforms = [data, ...orgPlatforms];
        this.$store.commit("requestPreparing/SET_ORGANIZATIONS_PLATFORMS", {
          orgId: this.selectedOrganization.id,
          platforms: updatedPlatforms,
        });
        this.$store.commit("requestPreparing/SET_SELECTED_PLATFORM", data);
        this.$router.push("/#participate");
      } catch (error) {
        console.log("error", error);
        if (error?.response?.status === 422) {
          const serverErrors = error?.response?.data?.errors;
          if (!serverErrors?.length) {
            this.error = [
              `Возникла ошибка при создании площадки. Код ошибки: ${error.response.status}`,
            ];
            this.pending = false;
            return;
          }
          const parsedErrors = serverErrors.reduce((acc, item) => {
            if (item.field === "contacts[0].value") {
              acc["phone"] = [item.code || "ошибка в данных номера телефона"];
              return acc;
            }
            if (item.field === "latitude" || item.field === "longitude") {
              acc["address"] = [item.code || "ошибка в данных адреса"];
              return acc;
            }
            acc[item.field] = [item.code || "ошибка в данных поля"];
            return acc;
          }, {});
          this.$refs.form.setErrors(parsedErrors);
          this.$nextTick(this.scrollToError);
          this.pending = false;
          return;
        }
        let errorMessage;
        if (error?.response?.data) {
          errorMessage = error.response.data;
          errorMessage += error?.response?.status
            ? "Код ошибки: " + error.response.status
            : "";
        } else {
          errorMessage = `Произошла ошибка.${
            error?.response?.status
              ? " Код ошибки: " + error.response.status
              : ""
          }`;
        }
        console.log("THIS IS THE ERROR MESSAGE", errorMessage);

        this.error = [errorMessage];
        console.log("error %O", error);
      }
      this.pending = false;
    },
  },
};
</script>

<style lang="less" scoped>
.form-group {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}
.box {
  background-color: #fff;
  padding: 20px;
  box-shadow: 0px 10px 30px rgba(44, 85, 94, 0.15);
  width: 100%;
  gap: 20px;
  flex-direction: column;
  display: flex;
  margin-bottom: 60px;
}
.title {
  font-size: @font-size-h2;
  line-height: @line-height-h2;
  font-weight: bold;
}
.md-error {
  margin-top: 10px;
}
.buttons {
  display: flex;
  gap: 10px;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-top: 30px;
}
</style>
